import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceDot,
} from 'recharts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TrendingDown, AlertTriangle, Calendar, Target, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { addMonths, format } from 'date-fns';
import { analyseFundingHistory, projectOwnership } from '@/utils/equityMath';
import type { ProjectionScenarioResult } from '@/utils/equityMath';

interface CompanyFinancials {
  monthly_burn?: number | null;
  cash_on_hand?: number | null;
}

interface FundingRound {
  id: string;
  closed_at: string;
  amount_raised?: number;
  pre_money_valuation?: number;
  post_money_valuation?: number;
  shares_issued?: number;
  instrument: string;
  total_amount?: number;
  share_price?: number;
}

type ProjectionScenario = ProjectionScenarioResult;

interface ProjectionData {
  month: string;
  monthNum: number;
  currentOwnership: number;
  conservative?: number;
  moderate?: number;
  aggressive?: number;
  eventLabel?: string;
}

interface OwnershipProjectionProps {
  companyId: string;
  currentOwnership: number;
  shareholderId: string;
}

export function OwnershipProjectionModel({
  companyId,
  currentOwnership,
  shareholderId,
}: OwnershipProjectionProps) {
  const [fundingHistory, setFundingHistory] = useState<FundingRound[]>([]);
  const [financials, setFinancials] = useState<CompanyFinancials | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (companyId) {
      fetchData();
    }
  }, [companyId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch funding rounds
      const { data: rounds, error: roundsError } = await supabase
        .from('funding_rounds')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_applied', true)
        .order('closed_at', { ascending: true });

      if (roundsError) throw roundsError;

      // Fetch company financials for runway
      const { data: financialsData, error: financialsError } = await supabase
        .from('company_financials')
        .select('*')
        .eq('company_id', companyId)
        .order('updated_at', { ascending: false })
        .limit(1)
        .single();

      if (financialsError && financialsError.code !== 'PGRST116') throw financialsError;

      setFundingHistory(rounds || []);
      setFinancials(financialsData);
    } catch (error) {
      console.error('Error fetching projection data:', error);
      toast.error('Failed to load projection data');
    } finally {
      setLoading(false);
    }
  };

  const analysis = useMemo(
    () =>
      analyseFundingHistory(
        fundingHistory.map((r) => ({
          closedAt: r.closed_at ?? null,
          totalAmount: r.total_amount ?? null,
          preMoneyValuation: r.pre_money_valuation ?? null,
        })),
        financials
          ? {
              monthlyBurn: financials.monthly_burn ?? null,
              cashOnHand: financials.cash_on_hand ?? null,
            }
          : null
      ),
    [fundingHistory, financials]
  );

  const scenarios = useMemo<ProjectionScenario[]>(
    () => (fundingHistory.length ? projectOwnership(analysis, currentOwnership) : []),
    [analysis, currentOwnership, fundingHistory.length]
  );

  const chartData = useMemo<ProjectionData[]>(() => {
    if (!scenarios.length) return [];

    const data: ProjectionData[] = [];
    const maxMonths = Math.max(...scenarios.map((s) => s.nextRoundMonths)) + 6;

    for (let i = 0; i <= maxMonths; i++) {
      const dataPoint: ProjectionData = {
        month: `Month ${i}`,
        monthNum: i,
        currentOwnership,
      };

      // Add scenario projections at their respective times
      scenarios.forEach((scenario) => {
        if (i >= scenario.nextRoundMonths) {
          const key = scenario.name.toLowerCase() as 'conservative' | 'moderate' | 'aggressive';
          dataPoint[key] = scenario.projectedOwnership;

          if (i === scenario.nextRoundMonths) {
            dataPoint.eventLabel = scenario.name;
          }
        }
      });

      data.push(dataPoint);
    }

    return data;
  }, [scenarios, currentOwnership]);

  const multiRoundProjection = useMemo(() => {
    if (!scenarios.length) return [];

    // Project 3 rounds into the future for moderate scenario
    const moderateScenario = scenarios.find((s) => s.name === 'Moderate');
    if (!moderateScenario) return [];

    let ownership = currentOwnership;
    const rounds = [];

    for (let i = 1; i <= 3; i++) {
      ownership = ownership * (1 - moderateScenario.dilution / 100);
      rounds.push({
        roundNumber: i,
        monthsFromNow: moderateScenario.nextRoundMonths * i,
        projectedDate: format(
          addMonths(new Date(), moderateScenario.nextRoundMonths * i),
          'MMM yyyy'
        ),
        ownership: ownership,
        cumulativeDilution: ((currentOwnership - ownership) / currentOwnership) * 100,
      });
    }

    return rounds;
  }, [scenarios, currentOwnership]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading ownership projections...</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (!fundingHistory.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Ownership Projection Model
          </CardTitle>
          <CardDescription>No funding history available to generate projections</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5" />
          Ownership Projection Model
          <Badge variant="secondary" className="ml-auto">
            <Sparkles className="h-3 w-3 mr-1" />
            AI-Powered
          </Badge>
        </CardTitle>
        <CardDescription>
          Predictive modeling based on {analysis?.totalRounds} historical funding rounds
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Key Insights */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-sm text-muted-foreground">Next Round Expected</div>
              <div className="text-2xl font-bold flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                {analysis?.monthsToNextRound}mo
              </div>
              {analysis?.runwayMonths && (
                <div className="text-xs text-muted-foreground mt-1">
                  Based on {analysis.runwayMonths}mo runway
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-sm text-muted-foreground">Avg Round Interval</div>
              <div className="text-2xl font-bold">
                {Math.round(analysis?.avgMonthsBetweenRounds || 0)}mo
              </div>
              <div className="text-xs text-muted-foreground mt-1">Historical average</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-sm text-muted-foreground">Avg Dilution</div>
              <div className="text-2xl font-bold text-red-500">
                {analysis?.avgDilution.toFixed(1)}%
              </div>
              <div className="text-xs text-muted-foreground mt-1">Per funding round</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-sm text-muted-foreground">Total Raised</div>
              <div className="text-2xl font-bold">
                $
                {(analysis?.totalRaised || 0) >= 1000000
                  ? ((analysis?.totalRaised || 0) / 1000000).toFixed(1) + 'M'
                  : (analysis?.totalRaised || 0).toLocaleString()}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Across {analysis?.totalRounds} rounds
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Warning Alert */}
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            These projections are estimates based on historical patterns and current runway. Actual
            results may vary significantly based on market conditions, company performance, and
            strategic decisions.
          </AlertDescription>
        </Alert>

        {analysis.assumptions.length > 0 && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <p className="font-medium mb-2">
                Some of this projection is assumed rather than measured
                {analysis.evidence === 'none' && ' — none of it rests on your funding history'}.
              </p>
              <ul className="list-disc ml-5 space-y-1 text-sm">
                {analysis.assumptions.map((assumption, i) => (
                  <li key={i}>{assumption.reason}</li>
                ))}
              </ul>
              <p className="text-xs mt-2">
                Measured from your history: {analysis.observedIntervals} round gap
                {analysis.observedIntervals === 1 ? '' : 's'}, {analysis.observedDilutions} dilution
                {analysis.observedDilutions === 1 ? '' : 's'}, {analysis.observedRoundSizes} round
                size
                {analysis.observedRoundSizes === 1 ? '' : 's'}.
              </p>
            </AlertDescription>
          </Alert>
        )}

        {/* Tabs for different views */}
        <Tabs defaultValue="scenarios" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="scenarios">Next Round Scenarios</TabsTrigger>
            <TabsTrigger value="chart">Visual Projection</TabsTrigger>
            <TabsTrigger value="multi-round">3-Round Outlook</TabsTrigger>
          </TabsList>

          <TabsContent value="scenarios">
            <div className="space-y-4">
              {scenarios.map((scenario, index) => (
                <Card key={scenario.name} className={index === 1 ? 'border-primary' : ''}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{scenario.name} Scenario</CardTitle>
                      <Badge
                        variant={
                          scenario.confidence === 'high'
                            ? 'default'
                            : scenario.confidence === 'medium'
                              ? 'secondary'
                              : 'outline'
                        }
                      >
                        {scenario.confidence} confidence
                      </Badge>
                    </div>
                    <CardDescription>{scenario.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <div className="text-sm text-muted-foreground">Expected In</div>
                        <div className="text-xl font-bold">{scenario.nextRoundMonths} months</div>
                        <div className="text-xs text-muted-foreground">
                          {format(addMonths(new Date(), scenario.nextRoundMonths), 'MMM yyyy')}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Est. Round Size</div>
                        <div className="text-xl font-bold">
                          $
                          {scenario.roundSize >= 1000000
                            ? (scenario.roundSize / 1000000).toFixed(1) + 'M'
                            : (scenario.roundSize / 1000).toFixed(0) + 'K'}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Expected Dilution</div>
                        <div className="text-xl font-bold text-red-500 flex items-center gap-1">
                          <TrendingDown className="h-4 w-4" />
                          {scenario.dilution.toFixed(1)}%
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Your Ownership After</div>
                        <div className="text-xl font-bold text-primary">
                          {scenario.projectedOwnership.toFixed(2)}%
                        </div>
                        <div className="text-xs text-muted-foreground">
                          from {currentOwnership.toFixed(2)}%
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="chart">
            <div className="space-y-4">
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="monthNum"
                    label={{ value: 'Months from Today', position: 'insideBottom', offset: -5 }}
                  />
                  <YAxis
                    label={{ value: 'Ownership %', angle: -90, position: 'insideLeft' }}
                    domain={[0, 'auto']}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-background border rounded-lg p-3 shadow-lg">
                            <p className="font-semibold">{data.month}</p>
                            {data.eventLabel && (
                              <p className="text-sm text-primary">⚡ {data.eventLabel} Round</p>
                            )}
                            <div className="mt-2 space-y-1">
                              <p className="text-sm">
                                Current: {data.currentOwnership?.toFixed(2)}%
                              </p>
                              {data.conservative && (
                                <p className="text-sm text-green-600">
                                  Conservative: {data.conservative.toFixed(2)}%
                                </p>
                              )}
                              {data.moderate && (
                                <p className="text-sm text-blue-600">
                                  Moderate: {data.moderate.toFixed(2)}%
                                </p>
                              )}
                              {data.aggressive && (
                                <p className="text-sm text-red-600">
                                  Aggressive: {data.aggressive.toFixed(2)}%
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="currentOwnership"
                    stroke="hsl(var(--muted-foreground))"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    name="Current"
                    dot={false}
                  />
                  <Line
                    type="stepAfter"
                    dataKey="conservative"
                    stroke="hsl(var(--chart-2))"
                    strokeWidth={2}
                    name="Conservative"
                    dot={(props) =>
                      props.payload.eventLabel ? (
                        <ReferenceDot {...props} r={6} fill="hsl(var(--chart-2))" />
                      ) : null
                    }
                  />
                  <Line
                    type="stepAfter"
                    dataKey="moderate"
                    stroke="hsl(var(--primary))"
                    strokeWidth={3}
                    name="Moderate (Most Likely)"
                    dot={(props) =>
                      props.payload.eventLabel ? (
                        <ReferenceDot {...props} r={6} fill="hsl(var(--primary))" />
                      ) : null
                    }
                  />
                  <Line
                    type="stepAfter"
                    dataKey="aggressive"
                    stroke="hsl(var(--destructive))"
                    strokeWidth={2}
                    name="Aggressive"
                    dot={(props) =>
                      props.payload.eventLabel ? (
                        <ReferenceDot {...props} r={6} fill="hsl(var(--destructive))" />
                      ) : null
                    }
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </TabsContent>

          <TabsContent value="multi-round">
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Projection of your ownership stake over the next 3 funding rounds (moderate
                scenario)
              </p>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Round</TableHead>
                    <TableHead>Expected Date</TableHead>
                    <TableHead>Months Away</TableHead>
                    <TableHead className="text-right">Projected Ownership</TableHead>
                    <TableHead className="text-right">Cumulative Dilution</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="bg-muted/50">
                    <TableCell className="font-medium">Current</TableCell>
                    <TableCell>Today</TableCell>
                    <TableCell>0</TableCell>
                    <TableCell className="text-right font-semibold">
                      {currentOwnership.toFixed(2)}%
                    </TableCell>
                    <TableCell className="text-right">—</TableCell>
                  </TableRow>
                  {multiRoundProjection.map((round) => (
                    <TableRow key={round.roundNumber}>
                      <TableCell className="font-medium">Round {round.roundNumber}</TableCell>
                      <TableCell>{round.projectedDate}</TableCell>
                      <TableCell>{round.monthsFromNow} months</TableCell>
                      <TableCell className="text-right font-semibold">
                        {round.ownership.toFixed(2)}%
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="destructive">
                          <TrendingDown className="h-3 w-3 mr-1" />-
                          {round.cumulativeDilution.toFixed(1)}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Alert className="mt-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  After 3 rounds, your ownership could decrease from {currentOwnership.toFixed(2)}%
                  to {multiRoundProjection[multiRoundProjection.length - 1]?.ownership.toFixed(2)}%
                  (
                  {(
                    (1 -
                      multiRoundProjection[multiRoundProjection.length - 1]?.ownership /
                        currentOwnership) *
                    100
                  ).toFixed(1)}
                  % total dilution). Consider participating in future rounds to maintain your stake.
                </AlertDescription>
              </Alert>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
